import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Tab,
  Tabs,
  Chip,
  CircularProgress,
  Badge,
  Container,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import EquipmentList from '../../components/reminders/EquipmentList';
import MaintenanceSchedule from '../../components/reminders/MaintenanceSchedule';
import SubscriptionStatus from '../../components/reminders/SubscriptionStatus';
import ServiceHeader from '../../components/services/ServiceHeader';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`equipment-tabpanel-${index}`}
      aria-labelledby={`equipment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const EquipmentDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [equipment, setEquipment] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalEquipment: 0,
    upcomingMaintenance: 0,
    overdueItems: 0,
    nextMaintenance: null as any,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is authenticated with service token
    const serviceToken = localStorage.getItem('serviceToken');
    const serviceUserData = localStorage.getItem('serviceUserData');
    
    if (!serviceToken || !serviceUserData) {
      // Not authenticated, redirect to services portal
      navigate('/app/services');
      return;
    }
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user data from service auth
      const serviceUserData = localStorage.getItem('serviceUserData');
      
      if (!serviceUserData) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }
      
      const userData = JSON.parse(serviceUserData);
      const serviceUserId = userData.id;

      // Check subscription status
      try {
        const subResponse = await api.get(`/reminders/subscription/${serviceUserId}`);
        setSubscription(subResponse.data);

        if (subResponse.data && subResponse.data.is_active) {
          // Load equipment and schedules
          const [equipmentRes, schedulesRes] = await Promise.all([
            api.get(`/reminders/equipment/${subResponse.data.id}`),
            api.get(`/reminders/schedules/${subResponse.data.id}`),
          ]);

          setEquipment(equipmentRes.data);
          setSchedules(schedulesRes.data);

          // Calculate stats
          const now = new Date();
          const upcoming = schedulesRes.data.filter((s: any) => 
            s.status === 'pending' && new Date(s.due_date) > now
          );
          const overdue = schedulesRes.data.filter((s: any) => 
            s.status === 'overdue' || (s.status === 'pending' && new Date(s.due_date) < now)
          );
          const nextSchedule = upcoming.sort((a: any, b: any) => 
            new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
          )[0];

          setStats({
            totalEquipment: equipmentRes.data.filter((e: any) => e.is_active).length,
            upcomingMaintenance: upcoming.length,
            overdueItems: overdue.length,
            nextMaintenance: nextSchedule,
          });
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          // Not subscribed yet
          setSubscription(null);
        } else {
          throw err;
        }
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(t('reminders.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };


  const handleLogout = () => {
    // Clear service authentication and redirect to reminders page
    localStorage.removeItem('serviceToken');
    localStorage.removeItem('serviceUserData');
    // Dispatch custom event to notify auth context
    window.dispatchEvent(new Event('serviceAuthChanged'));
    navigate('/');
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getUrgencyColor = (schedule: any) => {
    const now = new Date();
    const dueDate = new Date(schedule.due_date);
    const daysUntil = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) return 'error';
    if (daysUntil === 0) return 'error';
    if (daysUntil <= 7) return 'warning';
    if (daysUntil <= 30) return 'info';
    return 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!subscription || !subscription.is_active) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <BuildIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('reminders.subscription.notActive')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('reminders.subscription.contactSupport')}
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={handleLogout}
              sx={{ mt: 2 }}
            >
              {t('reminders.login.logout')}
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      {/* Service Header */}
      <ServiceHeader 
        serviceId="equipment"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Subscription Status */}
      <SubscriptionStatus 
        subscription={subscription} 
        onUpdate={loadData}
      />

      {/* Stats Cards */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)'
        },
        gap: 2,
        mb: 4,
        px: 2
      }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  {t('reminders.dashboard.totalEquipment')}
                </Typography>
                <Typography variant="h4">
                  {stats.totalEquipment}
                </Typography>
              </Box>
              <BuildIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    {t('reminders.dashboard.upcomingMaintenance')}
                  </Typography>
                  <Typography variant="h4">
                    {stats.upcomingMaintenance}
                  </Typography>
                </Box>
                <ScheduleIcon sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
        </Card>

        <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    {t('reminders.dashboard.overdueItems')}
                  </Typography>
                  <Typography variant="h4" color={stats.overdueItems > 0 ? 'error' : 'text.primary'}>
                    {stats.overdueItems}
                  </Typography>
                </Box>
                <Badge badgeContent={stats.overdueItems} color="error">
                  <WarningIcon sx={{ fontSize: 40, color: stats.overdueItems > 0 ? 'error.main' : 'text.disabled' }} />
                </Badge>
              </Box>
            </CardContent>
        </Card>

        <Card>
            <CardContent>
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  {t('reminders.dashboard.nextMaintenance')}
                </Typography>
                {stats.nextMaintenance ? (
                  <>
                    <Typography variant="body2" noWrap>
                      {stats.nextMaintenance.equipment?.brand} {stats.nextMaintenance.equipment?.model}
                    </Typography>
                    <Chip
                      size="small"
                      label={new Date(stats.nextMaintenance.due_date).toLocaleDateString()}
                      color={getUrgencyColor(stats.nextMaintenance)}
                      sx={{ mt: 1 }}
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('common.none')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
      </Box>

      {/* Tabs */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={t('reminders.myEquipment')} 
            icon={<Badge badgeContent={stats.totalEquipment} color="primary"><BuildIcon /></Badge>}
            iconPosition="start"
          />
          <Tab 
            label={t('reminders.upcomingMaintenance')} 
            icon={<Badge badgeContent={stats.overdueItems} color="error"><ScheduleIcon /></Badge>}
            iconPosition="start"
          />
          <Tab 
            label={t('reminders.maintenanceHistory')} 
            icon={<CheckCircleIcon />}
            iconPosition="start"
          />
          <Tab 
            label={t('reminders.notifications.title')} 
            icon={<SettingsIcon />}
            iconPosition="start"
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <EquipmentList 
            equipment={equipment} 
            subscription={subscription}
            onUpdate={loadData}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <MaintenanceSchedule 
            schedules={schedules.filter(s => s.status === 'pending')}
            equipment={equipment}
            onUpdate={loadData}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <MaintenanceSchedule 
            schedules={schedules.filter(s => s.status === 'completed')}
            equipment={equipment}
            onUpdate={loadData}
            showCompleted
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('reminders.notifications.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('reminders.notifications.reminderIntervals')}:
            </Typography>
            <Box sx={{ mt: 2 }}>
              {[30, 14, 7, 1].map(days => (
                <Chip
                  key={days}
                  label={t(`reminders.notifications.${days}days`)}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </TabPanel>
      </Card>
    </Container>
  );
};

export default EquipmentDashboard;